import React from 'react';
import { useFormContext } from 'react-hook-form';

import { ClassNames } from '@emotion/react';
import { I18n } from '@lingui/core';
import { t } from '@lingui/macro';
import tw from 'twin.macro';

import Form from '$/components/ui/Form';
import { PageMode } from '$/components/ui/Input/PageBuilder.types';
import Modal from '$/components/ui/Modal';
import { colorWithAlpha } from '$/utils/colors';
import { langDirClassname } from '$/shared/frontend/utils/i18n';
import { withProperties } from '$/utils/type';
import { useWindowSizeInfo } from '$/utils/useWindowSize';

import { usePageEditorContext } from '../PageEditorContext';
import { sectionsNodeObserver } from '../PageSections/sectionsNodeObserver';

const RegistrationPageFormModal: React.FC<{
  children: React.ReactNode;
  isWidget?: boolean;
  unlockedModal?: boolean;

  onClose?: () => void;
}> = ({ children, isWidget, onClose, unlockedModal }) => {
  const { language, mode, themeSettings } = usePageEditorContext();

  const [modalHeight, setModalHeight] = React.useState<number>(0);

  React.useEffect(() => {
    if (mode !== PageMode.edit) {
      return;
    }

    // DD: This is done so we can customize the height of the Modal
    // dynamically on top of the Registration Page Editor which in
    // Edit mode scrolls around

    const observableListener = (height: number): void => {
      setModalHeight(height);
    };
    sectionsNodeObserver.subscribe(observableListener);

    return (): void => {
      sectionsNodeObserver.unsubscribe(observableListener);
    };
  }, [mode]);

  const dir = mode === PageMode.edit ? 'ltr' : langDirClassname(language);

  return (
    <ClassNames>
      {({ css, cx }): React.ReactElement => {
        return (
          <Modal
            closeOnClickOutside={false}
            onClose={onClose}
            className={cx(
              `
        ${dir},
      `,
              css`
                ${tw`w-35e max-w-35e`}
                ${mode === PageMode.preview ? 'max-height: calc(100vh - 8rem);' : ''}
        ${mode === PageMode.edit ? 'max-height: calc(100vh - 6rem);' : ''}
        input:not([type='tel']) {
                  text-align: ${dir === 'rtl' ? 'right' : 'left'} !important;
                }

                ${Form.Field} {
                  margin-bottom: 0;
                }
                .reg-field,
                ${Form.Field}.reg-field {
                  margin-bottom: 1.5rem;

                  &:last-child {
                    margin-bottom: 0;
                  }
                }
              `,
              mode === PageMode.edit &&
                css`
                  ${tw`overflow-hidden bg-transparent shadow-none! w-45e! max-w-45e! z-under-header!`}
                  transform: translateX(-5rem);
                  &::before {
                    content: ' ';
                    top: 1rem;
                    left: 10rem;
                    z-index: -1;
                    height: calc(100% - 5rem);
                    ${tw`absolute bg-white w-35e shadow-2xl`}
                  }
                  .modal-header {
                    ${tw`ml-10e rounded-tl-lg`}
                  }
                  .modal-body {
                    ${tw`bg-transparent overflow-x-hidden pl-11e!`}
                  }
                  .modal-footer {
                    ${tw`ml-10e`}
                  }
                `
            )}
            backdropClassName={cx(
              `
      RegistrationFormModal
      ${mode === PageMode.edit ? 'absolute md:h-full' : ''}
    `,
              css`
                font-family: '${themeSettings.font ?? 'unset'}' !important;
                ${mode === PageMode.preview ? `z-index: 9999190 !important;` : ''}
                ${mode === PageMode.edit
                  ? `
         overflow-x: hidden;
         top: 54px !important;
         max-height: ${modalHeight ? modalHeight + 'px' : '100%'};
         z-index: 15 !important;
        `
                  : ''}
        ${isWidget
                  ? `
          background: transparent !important;
          backdrop-filter: none !important;
        `
                  : ''}
              `
            )}
            lock={mode !== PageMode.edit && !unlockedModal}
            anchorToTop={mode === PageMode.edit}
          >
            {children}
          </Modal>
        );
      }}
    </ClassNames>
  );
};

const RegistrationPageFormModalHeader: React.FC<{
  authorized?: boolean;
  isWidget?: boolean;
  children: React.ReactNode;
}> = ({ authorized, children, isWidget }) => {
  const { themeSettings } = usePageEditorContext();
  const { md: isWidthMd } = useWindowSizeInfo();
  return (
    <ClassNames>
      {({ css }): React.ReactElement => {
        return (
          <Modal.Header
            className={css`
              align-items: flex-start;
              background-color: ${isWidthMd || isWidget
                ? colorWithAlpha(themeSettings.primaryColor, 0.05)
                : '#fff'} !important;
              ${!authorized ? 'z-index: inherit !important' : ''}
              ${tw`py-1e px-1e`}
            `}
          >
            <Modal.Title className='modal-title flex justify-start items-baseline w-full pr-0 py-0'>
              {children}
            </Modal.Title>
          </Modal.Header>
        );
      }}
    </ClassNames>
  );
};

const RegistrationPageFormModalBody: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  const {
    formState: { errors },
  } = useFormContext();
  const hasErrors = Object.keys(errors)?.length > 0;
  return (
    <ClassNames>
      {({ css, cx }): React.ReactElement => {
        return (
          <Modal.Body
            className={cx(
              `modal-body px-1e pt-1e pb-1_5e ${hasErrors ? 'errors' : ''}`,
              css`
                position: relative;
                z-index: 9999016; // show capcha on mobile
              `
            )}
          >
            <div
              className={cx(
                '',
                css`
                  .grecaptcha-badge {
                    visibility: hidden;
                  }
                `
              )}
            >
              {children}
            </div>
          </Modal.Body>
        );
      }}
    </ClassNames>
  );
};

const RegistrationPageFormModalFooter: React.FC<{
  children: React.ReactNode;
  i18n: I18n;
}> = ({ children, i18n }) => {
  const { mode, themeSettings } = usePageEditorContext();
  return (
    <ClassNames>
      {({ css }): React.ReactElement => {
        return (
          <Modal.Footer
            className={css`
              ${tw`sm:px-1e`}
              button {
                border-radius: ${themeSettings.borderRadius}px !important;
              }
            `}
          >
            {mode !== PageMode.edit && (
              <Form.ErrorMessage
                message={t(i18n)`Scroll and verify all fields are valid`}
                className='mb-4 text-center mt-0'
              />
            )}
            {/* form submit */}
            <div className='flex justify-between footer-wrapper w-full items-center'>
              {children}
            </div>
          </Modal.Footer>
        );
      }}
    </ClassNames>
  );
};

export default withProperties(RegistrationPageFormModal, {
  Header: RegistrationPageFormModalHeader,
  Body: RegistrationPageFormModalBody,
  Footer: RegistrationPageFormModalFooter,
});
